import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, MessageCircle, Users, LogOut, User, Bookmark, Eye, ShieldOff, BellOff, Shield } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiClient } from '../api';
import { Button } from './ui/button';
import NotificationsBell from './NotificationsBell';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

const AppNav = () => {
  const { user, logout, wsSubscribe } = useAuth();
  const navigate = useNavigate();
  const [viewBadge, setViewBadge] = useState(0);

  // Initial fetch + realtime increment via the shared WebSocket
  useEffect(() => {
    if (!user) return;
    let mounted = true;
    apiClient.get('/views/me/count')
      .then((r) => { if (mounted) setViewBadge(r.data?.count || 0); })
      .catch(() => {});
    const unsub = wsSubscribe((data) => {
      if (data?.type === 'profile_view') setViewBadge((c) => c + 1);
    });
    return () => { mounted = false; unsub(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, wsSubscribe]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className="sticky top-0 z-30 bg-fnl-dark/95 backdrop-blur-md border-b border-purple-900/40">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-3 flex items-center justify-between">
        <Link to="/browse" className="flex items-center gap-2" data-testid="nav-logo">
          <div className="relative">
            <span className="text-white font-bold text-2xl">F</span>
            <Heart className="absolute -top-0.5 -right-2.5 w-3 h-3 fill-fnl-pink text-fnl-pink" />
          </div>
          <span className="text-white font-semibold hidden sm:block">Flirty</span>
        </Link>

        <div className="flex items-center gap-1 md:gap-2">
          <Link to="/browse" data-testid="nav-browse">
            <Button variant="ghost" className="text-white hover:text-fnl-pink hover:bg-white/5">
              <Users className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Browse</span>
            </Button>
          </Link>
          <Link to="/views" data-testid="nav-views" className="relative">
            <Button variant="ghost" className="text-white hover:text-fnl-pink hover:bg-white/5">
              <Eye className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Views</span>
              {viewBadge > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 md:static md:ml-1.5 bg-fnl-pink text-white text-[10px] leading-none rounded-full px-1.5 py-0.5 font-bold"
                  data-testid="views-badge"
                >
                  {viewBadge > 99 ? '99+' : viewBadge}
                </span>
              )}
            </Button>
          </Link>
          <Link to="/favorites" data-testid="nav-favorites">
            <Button variant="ghost" className="text-white hover:text-fnl-pink hover:bg-white/5">
              <Bookmark className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Favorites</span>
            </Button>
          </Link>
          <Link to="/chat" data-testid="nav-messages">
            <Button variant="ghost" className="text-white hover:text-fnl-pink hover:bg-white/5">
              <MessageCircle className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Messages</span>
            </Button>
          </Link>

          <NotificationsBell />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="text-white hover:text-fnl-pink hover:bg-white/5 rounded-full ml-1" data-testid="nav-account">
                <div className="w-8 h-8 rounded-full bg-fnl-pink flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-fnl-purple border-purple-900 text-white">
              <DropdownMenuItem asChild className="cursor-pointer focus:bg-fnl-pink/20 focus:text-fnl-pink">
                <Link to="/me" data-testid="nav-myprofile"><User className="w-4 h-4 mr-2" />My Profile</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild className="cursor-pointer focus:bg-fnl-pink/20 focus:text-fnl-pink">
                <Link to="/blocked" data-testid="nav-blocked"><ShieldOff className="w-4 h-4 mr-2" />Blocked users</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild className="cursor-pointer focus:bg-fnl-pink/20 focus:text-fnl-pink">
                <Link to="/muted" data-testid="nav-muted"><BellOff className="w-4 h-4 mr-2" />Muted users</Link>
              </DropdownMenuItem>
              {user?.is_admin && (
                <>
                  <DropdownMenuSeparator className="bg-purple-900" />
                  <DropdownMenuItem asChild className="cursor-pointer focus:bg-fnl-pink/20 focus:text-fnl-pink">
                    <Link to="/admin" data-testid="nav-admin"><Shield className="w-4 h-4 mr-2 text-fnl-pink" />Admin Console</Link>
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator className="bg-purple-900" />
              <DropdownMenuItem onClick={handleLogout} className="cursor-pointer focus:bg-fnl-pink/20 focus:text-fnl-pink" data-testid="nav-logout">
                <LogOut className="w-4 h-4 mr-2" />Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </nav>
  );
};

export default AppNav;
